"use client";

/**
 * CoachSessionContext — owns a supervisor Listen/Whisper session for the whole
 * authenticated app.
 *
 * WHY THIS EXISTS (2026-07-25): the session used to live inside the Live Calls
 * page component, which had two consequences:
 *   1. Its unmount cleanup POSTed /coach/stop and cleared the widget state, so
 *      navigating to ANY other tab deliberately ended monitoring.
 *   2. Neither the floating widget nor UnifiedDialerContext had a socket or an
 *      AudioContext, so audio physically could not continue off-page.
 * Cross-page monitoring was therefore impossible, and the widget's eavesdrop UI
 * only ever appeared on the dashboard — as a duplicate of the console's own rail.
 *
 * Mounted above the router outlet, this provider keeps the Socket.IO connection,
 * the audio engine and the mic alive across navigation:
 *
 *   layout
 *    ├─ CoachSessionProvider   ← socket + audio + mic  (survives route changes)
 *    ├─ LiveCalls   (consumer: roster + monitor rail)
 *    └─ Widget      (consumer: pill + panel on other pages)
 *
 * The session now ends only on real signals: the supervisor stops it, the call
 * ends (`stream-ended`), or the socket permanently fails.
 *
 * State is ALSO mirrored into UnifiedDialerContext so the existing widget keeps
 * working unchanged — this provider must therefore sit INSIDE UnifiedDialerProvider.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { showToast } from "@/app/_module/utils/show-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { startCoachMic, type CoachMicHandle } from "@/app/_module/utils/coach-mic";
import { useCoachAudioEngine } from "@/app/(authenticated)/agency-dashboard/_module/hooks/useCoachAudioEngine";

export type CoachMode = "listen" | "whisper";

/** Minimum shape the provider needs to start a session. */
export interface CoachTarget {
  callSid: string;
  userId: string;
  agentName?: string;
  leadName?: string;
  contactName?: string;
}

interface CoachSessionState {
  isListening: boolean;
  callSid: string | null;
  streamSid: string | null;
  sessionId: string | null;
  mode: CoachMode;
  agentName: string;
  leadName: string;
  startedAt: Date | null;
}

const IDLE: CoachSessionState = {
  isListening: false,
  callSid: null,
  streamSid: null,
  sessionId: null,
  mode: "listen",
  agentName: "",
  leadName: "",
  startedAt: null,
};

interface CoachSessionValue extends CoachSessionState {
  /** Seconds connected. */
  duration: number;
  volume: number;
  setVolume: (v: number) => void;
  inboundLevel: number;
  micActive: boolean;
  micVolume: number;
  micLatched: boolean;
  followingAgentId: string | null;

  start: (target: CoachTarget, mode: CoachMode) => Promise<void>;
  stop: () => Promise<void>;
  /** Click-to-talk: latches the mic on/off (whisper only). */
  toggleMic: () => void;
  followAgent: (target: CoachTarget) => void;
  unfollowAgent: () => void;
  /** True while a start is in flight — lets the UI disable buttons. */
  isStarting: boolean;
  /** Direct analyser read for the canvas visualizer (no React state at 60fps). */
  readSpectrum: (out: Uint8Array<ArrayBuffer>) => number;

  // ── Audio devices ────────────────────────────────────────────────────────
  /** Headphones / speaker for monitor playback. null = browser default. */
  outputDeviceId: string | null;
  setOutputDeviceId: (deviceId: string | null) => void;
  /** Microphone used for whisper. null = browser default. */
  inputDeviceId: string | null;
  setInputDeviceId: (deviceId: string | null) => void;
  /** False after a failed setSinkId — the browser can't route output (Safari/FF). */
  outputRoutingSupported: boolean;
}

const CoachSessionContext = createContext<CoachSessionValue | null>(null);

export function useCoachSession(): CoachSessionValue {
  const ctx = useContext(CoachSessionContext);
  if (!ctx) throw new Error("useCoachSession must be used within CoachSessionProvider");
  return ctx;
}

export function CoachSessionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUserContext();
  const {
    startEavesdrop: mirrorStart,
    stopEavesdrop: mirrorStop,
    setEavesdropFollowAgent,
    updateEavesdropLead,
    setCoachMicActive,
    setCoachMicVolume,
    setCoachInboundLevel,
    eavesdropFollowingAgentId,
    eavesdropActive: mirrorActive,
  } = useUnifiedDialer();

  const audio = useCoachAudioEngine();

  const [session, setSession] = useState<CoachSessionState>(IDLE);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [inboundLevel, setInboundLevel] = useState(0);
  const [micActive, setMicActive] = useState(false);
  const [micVolume, setMicVolume] = useState(0);
  const [micLatched, setMicLatched] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  // Device choices persist across sessions so a supervisor sets their headset
  // once, not on every call.
  const [outputDeviceId, setOutputDeviceIdState] = useState<string | null>(null);
  const [inputDeviceId, setInputDeviceId] = useState<string | null>(null);
  const [outputRoutingSupported, setOutputRoutingSupported] = useState(true);
  const inputDeviceIdRef = useRef<string | null>(null);
  useEffect(() => { inputDeviceIdRef.current = inputDeviceId; }, [inputDeviceId]);

  const socketRef = useRef<any>(null);
  const micHandleRef = useRef<CoachMicHandle | null>(null);
  const micVolumeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startingRef = useRef(false);
  const micLatchedRef = useRef(false);
  useEffect(() => { micLatchedRef.current = micLatched; }, [micLatched]);

  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Tracks the CURRENT callSid to rejoin on reconnect — auto-follow moves it.
  const currentCallSidRef = useRef<string | null>(null);
  useEffect(() => { currentCallSidRef.current = session.callSid; }, [session.callSid]);

  // ── Mic ────────────────────────────────────────────────────────────────────
  const stopTalking = useCallback(() => {
    if (micVolumeIntervalRef.current) {
      clearInterval(micVolumeIntervalRef.current);
      micVolumeIntervalRef.current = null;
    }
    micHandleRef.current?.stop();
    micHandleRef.current = null;
    setMicVolume(0);
    setMicActive(false);
    setMicLatched(false);
    const socket = socketRef.current;
    if (socket?.connected) socket.emit("mic-stop");
  }, []);

  const startTalking = useCallback(async () => {
    const socket = socketRef.current;
    if (!socket || sessionRef.current.mode === "listen" || micHandleRef.current) return;
    try {
      socket.emit("mic-start");
      const handle = await startCoachMic(
        (base64Payload) => {
          if (socket.connected) socket.emit("mic-audio", { payload: base64Payload });
        },
        (err) => {
          showToast(`Mic error: ${err.message}`, "error");
          setMicActive(false);
          setMicLatched(false);
        },
        inputDeviceIdRef.current,
      );
      micHandleRef.current = handle;
      setMicActive(true);
      micVolumeIntervalRef.current = setInterval(() => {
        setMicVolume(handle.getVolumeLevel());
      }, 33);
    } catch (err) {
      showToast(`Could not start mic: ${(err as Error).message}`, "error");
      socket.emit("mic-stop");
    }
  }, []);

  const setOutputDeviceId = useCallback((deviceId: string | null) => {
    setOutputDeviceIdState(deviceId);
    void audio.setOutputDevice(deviceId).then((ok) => {
      setOutputRoutingSupported(ok || !deviceId);
      if (!ok && deviceId) {
        showToast(
          "This browser can't send audio to a specific device — using the system default.",
          "info",
        );
      }
    });
  }, [audio]);

  /** Click-to-talk latch: stays live until clicked again (no 20-minute mouse-hold). */
  const toggleMic = useCallback(() => {
    if (micHandleRef.current) {
      stopTalking();
    } else {
      setMicLatched(true);
      startTalking();
    }
  }, [startTalking, stopTalking]);

  // ── Teardown ───────────────────────────────────────────────────────────────
  const stop = useCallback(async () => {
    const s = sessionRef.current;

    if (micHandleRef.current) {
      try { micHandleRef.current.stop(); } catch { /* ignore */ }
      micHandleRef.current = null;
    }
    if (micVolumeIntervalRef.current) {
      clearInterval(micVolumeIntervalRef.current);
      micVolumeIntervalRef.current = null;
    }
    setMicActive(false);
    setMicVolume(0);
    setMicLatched(false);

    try {
      if (s.callSid) {
        await api.post({
          url: getApiUrl("/communication/coach/stop"),
          body: { callSid: s.callSid, streamSid: s.streamSid, sessionId: s.sessionId },
          auth: true,
        });
      }
    } catch {
      // Best effort — the stream may already be gone if the call ended.
    }

    const socket = socketRef.current;
    if (socket) {
      try { socket.disconnect?.(); } catch { /* ignore */ }
      socketRef.current = null;
    }
    await audio.teardown();
    setCoachInboundLevel(0);
    setInboundLevel(0);
    mirrorStop();

    setSession(IDLE);
    setDuration(0);
  }, [audio, mirrorStop, setCoachInboundLevel]);

  // ── Start ──────────────────────────────────────────────────────────────────
  const start = useCallback(async (target: CoachTarget, mode: CoachMode) => {
    if (startingRef.current) return;
    startingRef.current = true;
    setIsStarting(true);

    let startedOnServer = false;
    let succeeded = false;

    // Tear down any existing session first — otherwise Listen → Whisper stacks
    // parallel sockets all playing the same audio (summed amplitudes clip).
    if (sessionRef.current.isListening || socketRef.current) {
      await stop();
    }

    // AUDIO GRAPH MUST BE BUILT INSIDE THE USER GESTURE. Any await hands control
    // back to the event loop, and a context constructed outside the click's
    // activation window starts 'suspended' — every buffer is then silently
    // dropped (silent audio + flat waveform). createGraph needs nothing from the
    // server, so it runs before the REST call.
    audio.createGraph(volume, (level) => {
      setCoachInboundLevel(level);
      setInboundLevel(level);
    });

    try {
      // WRONG-INSTANCE AUTO-RETRY (2026-07-29, Moe Ray).
      //
      // The backend's stream registry is an in-memory Map and prod runs 2
      // instances, so this POST reaches the box holding the Twilio stream only
      // ~half the time. When it doesn't, the backend now replies 409
      // STREAM_ON_OTHER_INSTANCE (retryable) instead of a flat 503 — but a
      // retryable error is worthless unless something retries.
      //
      // Moe's 22:58 attempt was failed / failed / worked: three clicks, one
      // load-balancer coin flip each. He had to click until he got lucky, and
      // the toast just said "Failed to start listen". Each fresh POST is an
      // independent roll, so a handful of attempts makes landing on the wrong
      // box effectively invisible: 3 tries ≈ 12% miss, 5 ≈ 3%.
      //
      // Only retries the explicitly-retryable wrong-instance case — a genuinely
      // ended call still fails immediately rather than spinning.
      const MAX_ATTEMPTS = 5;
      let response: any = null;
      let data: any = null;
      let lastRetryable = false;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        response = await api.post<any>({
          url: getApiUrl("/communication/coach/start"),
          body: { callSid: target.callSid, mode },
          auth: true,
        });
        startedOnServer = true;

        data = response?.data?.data || response?.data;
        if (data?.streamSid && data?.callSid) break; // got it

        const errCode = data?.error || response?.data?.error;
        lastRetryable = errCode === "STREAM_ON_OTHER_INSTANCE";
        if (!lastRetryable || attempt === MAX_ATTEMPTS) break;

        // Brief pause so the next POST is a genuinely new connection/roll.
        await new Promise((r) => setTimeout(r, 400));
      }

      if (!data?.streamSid || !data?.callSid) {
        // Two different situations, deliberately styled differently:
        //
        //  retryable  → the call is live and fine, we just haven't hooked into
        //               its audio yet. Nothing is broken, so this is an INFO
        //               toast worded as "still connecting" — an error-red
        //               "failed" here makes a healthy call look dead and sends
        //               managers to support over a transient miss.
        //
        //  otherwise  → genuinely no stream (call ended). That IS a failure and
        //               keeps the error styling.
        //
        // No infrastructure wording in either — "another server" means nothing
        // to an agent. The topology stays in COACH_STREAM_ON_OTHER_INSTANCE.
        if (lastRetryable) {
          showToast(`Queuing up ${mode} — tap again in a second.`, "info");
        } else {
          showToast(`Failed to start ${mode}`, "error");
        }
        return;
      }

      // Joining an existing session locks the mode to whatever the first
      // supervisor chose — adopt it rather than showing the wrong mode.
      const effectiveMode: CoachMode = (data.mode as CoachMode) || mode;
      const joinedExisting = !!data.joinedExisting;
      if (joinedExisting && effectiveMode !== mode) {
        showToast(`Joined existing ${effectiveMode} session — mode locked by the first supervisor`, "info");
      } else if (joinedExisting) {
        showToast(`Joined ${effectiveMode} session already in progress`, "info");
      }

      const { io } = await import("socket.io-client");
      const { getSupabaseSession } = await import("@/app/_module/services/auth");
      const supaSession = await getSupabaseSession();
      const token = supaSession?.access_token || "";
      const agencyId =
        (user?.currentAgency as any)?.agencyId || (user?.currentAgency as any)?.agency?.id || "";
      const backendUrl = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:8080";

      const socket = io(`${backendUrl}/call-stream`, {
        auth: { token },
        query: { agencyId },
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 8000,
        timeout: 60000,
      });
      socketRef.current = socket;

      // Emit listen-start only after the gateway finishes auth. 'connect' fires
      // on handshake, but handleConnection awaits supabase.auth.getUser after
      // that; the gateway emits 'active-streams' when it's genuinely ready.
      let listenStartSent = false;
      const sendListenStart = () => {
        if (listenStartSent) return;
        listenStartSent = true;
        socket.emit("listen-start", { callSid: currentCallSidRef.current || target.callSid });
      };
      socket.on("active-streams", () => sendListenStart());
      socket.on("connect", () => {
        listenStartSent = false; // allow re-fire on reconnect
        setTimeout(sendListenStart, 600);
      });

      socket.on("mic-error", (d: { message: string }) => {
        showToast(d?.message || "Mic error — audio not reaching the call", "error");
        stopTalking();
      });
      socket.on("reconnect_attempt", (attempt: number) => {
        if (attempt > 1) showToast(`Reconnecting to call stream (attempt ${attempt})...`, "info");
      });
      socket.on("reconnect_failed", () => {
        showToast("Lost connection to call stream — please re-listen.", "error");
        stop();
      });
      socket.on("listen-error", (d: { message: string }) => {
        showToast(d?.message || "Listen error", "error");
        stop();
      });

      // WRONG-INSTANCE RETRY. Prod runs 2 backend instances and the Twilio media
      // stream lands on one of them while this socket lands independently, so
      // ~half of attempts reach an instance that has never seen the stream. The
      // gateway now distinguishes that from a genuinely-ended call and asks us to
      // reconnect; each fresh connection is an independent load-balancer roll, so
      // this converges quickly instead of failing outright. (A Redis socket.io
      // adapter would make this unnecessary — none is provisioned today.)
      let instanceRetries = 0;
      const MAX_INSTANCE_RETRIES = 6;
      socket.on("listen-retry", () => {
        instanceRetries++;
        if (instanceRetries > MAX_INSTANCE_RETRIES) {
          showToast(
            "Couldn't reach the server holding this call's audio. Please try again.",
            "error",
          );
          stop();
          return;
        }
        listenStartSent = false;
        // Force a NEW connection rather than socket.io's resume, which would
        // return us to the same instance.
        try { socket.disconnect(); } catch { /* ignore */ }
        setTimeout(() => { try { socket.connect(); } catch { /* ignore */ } }, 250);
      });

      // Silent-failure watchdog: no sound and a flat waveform look identical
      // whether the browser suspended the context, the gateway isn't relaying,
      // or Twilio never attached media. Name the cause instead of nothing.
      let framesReceived = 0;
      socket.on("audio", (d: { payload: string; track: string; callSid: string }) => {
        framesReceived++;
        audio.pushFrame(d.track, d.payload);
      });
      const watchdog = setTimeout(() => {
        if (framesReceived > 0) return;
        const state = audio.getAudioContext()?.state ?? "closed";
        showToast(
          state === "suspended"
            ? "Audio is blocked by the browser — click anywhere on the page to enable sound."
            : "Connected, but no audio is arriving from the call. The stream may not have attached.",
          "error",
        );
      }, 6000);
      socket.on("disconnect", () => clearTimeout(watchdog));

      socket.on("stream-ended", () => {
        showToast("Call ended — monitoring stopped", "info");
        stop();
      });

      // Auto-follow: when the agent's next call starts, follow it. Must move
      // callSid too, or Stop targets the ended call and the new Twilio stream
      // keeps running (and billing).
      socket.on("listen-started", (d: any) => {
        if (d.autoFollowed && d.callSid) {
          setSession((prev) => ({
            ...prev,
            callSid: d.callSid,
            streamSid: null,
            sessionId: null,
            leadName: d.leadName || prev.leadName,
            agentName: d.agentName || prev.agentName,
          }));
          updateEavesdropLead(d.leadName || "Lead");
          showToast(`Auto-followed to ${d.agentName}'s next call with ${d.leadName}`, "info");
        }
      });

      setSession({
        isListening: true,
        callSid: target.callSid,
        streamSid: data.streamSid,
        sessionId: data.sessionId || null,
        mode: effectiveMode,
        agentName: target.agentName || "Agent",
        leadName: target.leadName || target.contactName || "Lead",
        startedAt: new Date(),
      });
      setDuration(0);

      // Mirror into UnifiedDialerContext so the floating widget reflects the
      // session on other pages. autoOpenWidget=false: the console owns the UI
      // while you're on it; the widget maximizes itself when you navigate away.
      mirrorStart(
        target.callSid,
        data.streamSid || "",
        target.agentName || "Agent",
        target.leadName || target.contactName || "Lead",
        effectiveMode,
        false,
      );

      if (!joinedExisting) {
        showToast(
          `${effectiveMode === "whisper" ? "Whisper-coaching" : "Listening to"} ${target.agentName}'s call...`,
          "success",
        );
      }
      succeeded = true;
    } catch {
      showToast(`Failed to start ${mode}`, "error");
    } finally {
      // Server session created but local setup failed → end it so we don't leave
      // an orphaned session and a lingering "supervisor listening" banner.
      if (startedOnServer && !succeeded) {
        try {
          api.post({
            url: getApiUrl("/communication/coach/stop"),
            body: { callSid: target.callSid },
            auth: true,
          }).catch(() => {});
        } catch { /* ignore */ }
        await audio.teardown();
      }
      startingRef.current = false;
      setIsStarting(false);
    }
  }, [audio, stop, stopTalking, volume, user, mirrorStart, updateEavesdropLead, setCoachInboundLevel]);

  // ── Follow / unfollow ──────────────────────────────────────────────────────
  const followAgent = useCallback((target: CoachTarget) => {
    const socket = socketRef.current;
    if (!socket) {
      showToast("Start listening to a call first, then follow the agent", "info");
      return;
    }
    socket.emit?.("follow-agent", { agentUserId: target.userId });
    setEavesdropFollowAgent(target.userId);
    showToast(`Following ${target.agentName} — will auto-listen to all their calls`, "success");
  }, [setEavesdropFollowAgent]);

  const unfollowAgent = useCallback(() => {
    socketRef.current?.emit?.("unfollow-agent");
    setEavesdropFollowAgent(null);
    showToast("Stopped following agent", "info");
  }, [setEavesdropFollowAgent]);

  // ── Ambient effects (now app-wide, not page-scoped) ────────────────────────
  useEffect(() => {
    if (!session.isListening || !session.startedAt) return;
    const timer = setInterval(() => {
      setDuration(Math.floor((Date.now() - session.startedAt!.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [session.isListening, session.startedAt]);

  useEffect(() => { audio.applyVolume(volume); }, [volume, audio]);
  useEffect(() => { setCoachMicActive(micActive); }, [micActive, setCoachMicActive]);
  useEffect(() => { setCoachMicVolume(micVolume); }, [micVolume, setCoachMicVolume]);

  // Widget-driven PTT (the widget dispatches window events rather than owning
  // the socket). Works on any page now that the socket lives here.
  useEffect(() => {
    if (!session.isListening || session.mode === "listen") return;
    const onStart = () => { if (!micHandleRef.current) startTalking(); };
    const onStop = () => { if (micHandleRef.current) stopTalking(); };
    window.addEventListener("coach:request-mic-start", onStart);
    window.addEventListener("coach:request-mic-stop", onStop);
    return () => {
      window.removeEventListener("coach:request-mic-start", onStart);
      window.removeEventListener("coach:request-mic-stop", onStop);
    };
  }, [session.isListening, session.mode, startTalking, stopTalking]);

  // Spacebar = MOMENTARY talk, on any page. Never closes a click-latched mic,
  // and ignored while typing so it doesn't hijack normal text entry.
  useEffect(() => {
    if (!session.isListening || session.mode === "listen") return;

    const isTextInput = (el: EventTarget | null): boolean => {
      if (!el || !(el instanceof HTMLElement)) return false;
      const tag = el.tagName?.toLowerCase();
      return tag === "input" || tag === "textarea" || el.isContentEditable;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "Space" || e.repeat || isTextInput(e.target)) return;
      e.preventDefault();
      if (!micHandleRef.current) startTalking();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== "Space" || isTextInput(e.target)) return;
      e.preventDefault();
      if (micHandleRef.current && !micLatchedRef.current) stopTalking();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [session.isListening, session.mode, startTalking, stopTalking]);

  // The widget's Stop button clears only the mirrored context state; without
  // this the Twilio stream, socket and AudioContext would stay alive (and
  // billing) until the call ended on its own.
  useEffect(() => {
    if (!mirrorActive && session.isListening) stop();
  }, [mirrorActive, session.isListening, stop]);

  // NOTE: there is deliberately NO unmount teardown here. This provider lives
  // for the whole authenticated session, so a route change no longer ends
  // monitoring — that page-scoped cleanup is exactly what made the session get
  // lost when switching tabs. A full page unload closes the socket anyway, and
  // the server-side stream is reaped by the call-end webhook + orphan cron.

  const value = useMemo<CoachSessionValue>(() => ({
    ...session,
    duration,
    volume,
    setVolume,
    inboundLevel,
    micActive,
    micVolume,
    micLatched,
    followingAgentId: eavesdropFollowingAgentId ?? null,
    start,
    stop,
    toggleMic,
    followAgent,
    unfollowAgent,
    isStarting,
    readSpectrum: audio.readSpectrum,
    outputDeviceId,
    setOutputDeviceId,
    inputDeviceId,
    setInputDeviceId,
    outputRoutingSupported,
  }), [
    session, duration, volume, inboundLevel, micActive, micVolume, micLatched,
    eavesdropFollowingAgentId, start, stop, toggleMic, followAgent, unfollowAgent, isStarting,
    audio.readSpectrum,
    outputDeviceId, setOutputDeviceId, inputDeviceId, outputRoutingSupported,
  ]);

  return (
    <CoachSessionContext.Provider value={value}>
      {children}
    </CoachSessionContext.Provider>
  );
}
